import classNames from 'classnames';
import countries from 'i18n-iso-countries';
import React, { useState } from 'react';
import styles from './CheckIndividualTestMatches.module.scss';
import { ReactComponent as IconDropdown } from '../../../assets/images/icons/icon-chevron-right-black.svg';
import { IndividualTestMatch } from '@solid/shared';
import format from 'date-fns/format';
import { useTranslation } from 'react-i18next';
interface CheckIndividualTestMatchesProps {
  matches: IndividualTestMatch[];
}

const CheckIndividualTestMatches: React.FC<CheckIndividualTestMatchesProps> = ({ matches }) => {
  const [openEntries, setOpenEntries] = useState<string[]>([]);
  const { t } = useTranslation();
  return (
    <>
      <div className={styles.matchHeader}>
        <div className={classNames(styles.col, styles.dropdown)}></div>
        <span className={classNames(styles.col, styles.similarity)}>
          {t('checks.individualMultiTest.matches.similarity')}
        </span>
        <span className={classNames(styles.col, styles.name)}>{t('checks.individualMultiTest.matches.name')}</span>
        <span className={classNames(styles.col, styles.city)}>{t('checks.individualMultiTest.matches.city')}</span>
        <span className={classNames(styles.col, styles.country)}>
          {t('checks.individualMultiTest.matches.country')}
        </span>
        <span className={classNames(styles.col, styles.listAbbreviation)}>
          {t('checks.individualMultiTest.matches.restrictionList')}
        </span>
      </div>
      {matches.map(match => {
        const key = `${match.similarity}_${match.name}_${match.city}_${match.listAbbreviation}_${match.updateId}`;
        return (
          <div
            key={key}
            className={classNames(styles.matchEntry, {
              [styles.matchDetailsOpened]: openEntries.includes(key),
            })}>
            <div className={styles.matchHeader}>
              <div className={classNames(styles.col, styles.dropdown)}>
                <button
                  className={classNames(styles.dropdownBtn, {
                    [styles.dropdownBtnOpened]: openEntries.includes(key),
                  })}
                  onClick={() => {
                    openEntries.includes(key)
                      ? setOpenEntries(openEntries.filter(entry => entry !== key))
                      : setOpenEntries([...openEntries, key]);
                  }}>
                  <IconDropdown className={styles.dropdownIcon} />
                </button>
              </div>
              <div className={classNames(styles.col, styles.similarity)}>
                <span>{match.similarity}</span>
                <div className={classNames(styles.similarityIndicatorContainer)}>
                  <div
                    className={classNames(styles.similarityIndicator, {
                      [styles.similarityIndicator1]: parseInt(match.similarity) > 20,
                      [styles.similarityIndicator2]: parseInt(match.similarity) > 40,
                      [styles.similarityIndicator3]: parseInt(match.similarity) > 60,
                      [styles.similarityIndicator4]: parseInt(match.similarity) > 80,
                      [styles.similarityIndicator5]: parseInt(match.similarity) === 100,
                    })}></div>
                </div>
              </div>
              <span className={classNames(styles.col, styles.name)}>{match.name}</span>
              <span className={classNames(styles.col, styles.city)}>{match.city}</span>
              <span className={classNames(styles.col, styles.country)}>{match.countryISO}</span>
              <span className={classNames(styles.col, styles.listAbbreviation)}>{match.listAbbreviation}</span>
            </div>

            <div
              className={classNames(styles.matchDetails, {
                [styles.matchDetailsOpened]: openEntries.includes(key),
              })}>
              <div className={styles.matchDetailsSection}>
                <h3>{t('checks.individualMultiTest.matches.addressSource')}</h3>
                <div className='form-row'>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.listAbbreviation')}</label>
                    <div className='control'>
                      <input readOnly type='text' value={match.listAbbreviation} />
                    </div>
                  </div>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.listName')}</label>
                    <div className='control'>
                      <input readOnly type='text' value={match.listName} />
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.matchDetailsSection}>
                <h3>{t('checks.individualMultiTest.matches.info')}</h3>
                <div className='form-row'>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.addressType')}</label>
                    <div className='control'>
                      <input readOnly type='text' value={match.addressType} />
                    </div>
                  </div>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.nameBlock')}</label>
                    <div className='control'>
                      <input readOnly type='text' value={match.name} />
                    </div>
                  </div>
                </div>
                <div className='form-row'>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.comment')}</label>
                    <div className='control'>
                      <textarea readOnly rows={6} value={match.info} />
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.matchDetailsSection}>
                <h3>{t('checks.individualMultiTest.matches.addressData')}</h3>
                <div className='form-row'>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.street')}</label>
                    <div className='control'>
                      <input readOnly type='text' value={match.street} />
                    </div>
                  </div>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.city')}</label>
                    <div className='control'>
                      <input readOnly type='text' value={match.city} />
                    </div>
                  </div>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.country')}</label>
                    <div className='control'>
                      <input
                        readOnly
                        type='text'
                        value={
                          match.countryISO ? match.countryISO + ', ' + countries.getName(match.countryISO, 'de') : ''
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.matchDetailsSection}>
                <h3>{t('checks.individualMultiTest.matches.personalData')}</h3>
                <div className='form-row'>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.dateOfBirth')}</label>
                    <div className='control'>
                      <input readOnly type='text' value={match.dateOfBirth} />
                    </div>
                  </div>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.cityOfBirth')}</label>
                    <div className='control'>
                      <input readOnly type='text' value={match.cityOfBirth} />
                    </div>
                  </div>
                </div>
                <div className='form-row'>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.passportData')}</label>
                    <div className='control'>
                      <input readOnly type='text' value={match.passportData} />
                    </div>
                  </div>
                </div>
                <div className='form-row'>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.countryOfBirth')}</label>
                    <div className='control'>
                      <input
                        readOnly
                        type='text'
                        value={
                          match.countryISO
                            ? match.countryISO + ', ' + countries.getName(match.countryOfBirthISO, 'de')
                            : ''
                        }
                      />
                    </div>
                  </div>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.nationality')}</label>
                    <div className='control'>
                      <input
                        readOnly
                        type='text'
                        value={
                          match.countryISO
                            ? match.countryISO + ', ' + countries.getName(match.nationalityISO, 'de')
                            : ''
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.matchDetailsSection}>
                <h3>{t('checks.individualMultiTest.matches.prohibitionInformation')}</h3>
                <div className='form-row'>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.restrictionType')}</label>
                    <div className='control'>
                      <input readOnly type='text' value={match.restrictionType} />
                    </div>
                  </div>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.restrictionSource')}</label>
                    <div className='control'>
                      <input readOnly type='text' value={match.restrictionSource} />
                    </div>
                  </div>
                </div>
                <div className='form-row'>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.restrictionDate')}</label>
                    <div className='control'>
                      <input
                        readOnly
                        type='text'
                        value={match.restrictionDate ? format(new Date(match.restrictionDate), 'dd.MM.yyyy') : ''}
                      />
                    </div>
                  </div>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.aliasGroupNo')}</label>
                    <div className='control'>
                      <input readOnly type='text' value={match.aliasGroupNo} />
                    </div>
                  </div>
                </div>
                <div className='form-row'>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.updateDate')}</label>
                    <div className='control'>
                      <input
                        readOnly
                        type='text'
                        value={match.updateDate ? format(new Date(match.updateDate), 'dd.MM.yyyy') : ''}
                      />
                    </div>
                  </div>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.updateId')}</label>
                    <div className='control'>
                      <input readOnly type='text' value={match.updateId} />
                    </div>
                  </div>
                </div>
                <div className='form-row'>
                  <div className='form-field'>
                    <label>{t('checks.individualMultiTest.matches.sourceWebLink')}</label>
                    <div className='control'>
                      <input readOnly type='text' value={match.sourceWebLink} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default CheckIndividualTestMatches;
